/**
 * One-time Firebase Auth key health check (block 49).
 *
 * Why this exists: frontend/.env's VITE_FIREBASE_API_KEY was dead
 * (Google's API_KEY_INVALID / "API key expired") for five weeks before
 * anyone noticed — every sign-in attempt just failed with the Firebase JS
 * SDK's own generic `auth/internal-error`-shaped message, which never once
 * says the key itself is the problem. This makes that specific failure
 * loud and named instead of silent.
 *
 * Uses the exact same REST endpoint AuthContext.tsx's loginWithEmail already
 * calls (accounts:signInWithPassword) with garbage credentials — no side
 * effects (no account is created or touched), and it's the real, live
 * production path, not a synthetic proxy for it. A referrer restriction
 * (a DIFFERENT, non-fatal misconfiguration — see block 48) is deliberately
 * NOT flagged here: it 403s with PERMISSION_DENIED, not the 400
 * API_KEY_INVALID this check exists to catch, and unlike a dead key, a
 * correctly-referrer-restricted key works fine from the real browser this
 * code actually runs in.
 */

export interface AuthHealthResult {
  healthy: boolean;
  reason?: string;
}

export async function checkFirebaseAuthHealth(apiKey: string | undefined): Promise<AuthHealthResult> {
  if (!apiKey) {
    return { healthy: false, reason: 'VITE_FIREBASE_API_KEY is not set.' };
  }

  try {
    const res = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: 'emi-config-health-check@invalid.example',
          password: 'this-is-not-a-real-account',
          returnSecureToken: true,
        }),
      }
    );

    if (res.status === 200) return { healthy: true }; // would only happen if that email were real — harmless either way

    const body = await res.json().catch(() => null);
    const reason = body?.error?.details?.find((d: any) => d.reason)?.reason || body?.error?.message;

    if (reason === 'API_KEY_INVALID') {
      return { healthy: false, reason: 'API key expired or invalid (API_KEY_INVALID).' };
    }

    // Any other error (EMAIL_NOT_FOUND, INVALID_LOGIN_CREDENTIALS,
    // API_KEY_HTTP_REFERRER_BLOCKED, etc.) means the key itself was accepted
    // enough for Google to evaluate the request — healthy for this check's
    // purpose, whatever else might separately be true about it.
    return { healthy: true };
  } catch {
    // Network failure (offline, CORS in an unusual host environment) — not
    // evidence of a bad key. Fail open rather than showing a false alarm.
    return { healthy: true };
  }
}
